// ============================================================================
// CARPETA FORM
// ============================================================================
// Formulario unificado para crear/actualizar carpeta con ubicaciones y usuarios

import React from 'react';
import { DualListbox } from '../../../selectors';
import { useLanguage } from '../../../../contexts/LanguageContext';

export interface CarpetaFormData {
  carpeta: string;
  ubicacionids: number[];
  usuarioids: number[];
  carpetaid?: number; // Para modo actualizar
}

interface CarpetaFormProps {
  formData: CarpetaFormData;
  setFormData: (data: CarpetaFormData | ((prev: CarpetaFormData) => CarpetaFormData)) => void;
  ubicacionesOptions: Array<{ value: any; label: string }>;
  usuariosOptions: Array<{ value: any; label: string }>;
  loading?: boolean;
  onSave: () => void;
  onCancel: () => void;
  isUpdate?: boolean;
  themeColor?: 'green' | 'orange';
}

const CarpetaForm: React.FC<CarpetaFormProps> = ({
  formData,
  setFormData,
  ubicacionesOptions,
  usuariosOptions,
  loading = false,
  onSave,
  onCancel,
  isUpdate = false,
  themeColor = 'green'
}) => {
  const { t } = useLanguage();

  const themeClasses = {
    green: {
      text: 'text-green-500',
      focus: 'focus:ring-green-500 focus:border-green-500',
      button: 'bg-green-600 hover:bg-green-700'
    },
    orange: {
      text: 'text-orange-500',
      focus: 'focus:ring-orange-500 focus:border-orange-500',
      button: 'bg-orange-600 hover:bg-orange-700'
    }
  };

  const theme = themeClasses[themeColor];
  const isFormValid = (formData.carpeta || '').trim().length > 0;

  return (
    <div className="space-y-6">
      {/* 1. Nombre de carpeta */}
      <div>
        <label className={`block text-lg font-bold mb-2 font-mono tracking-wider ${theme.text}`}>
          CARPETA COMPARTIDA *
        </label>
        <input
          type="text"
          value={formData.carpeta || ''}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, carpeta: e.target.value }))
          }
          placeholder="CARPETA COMPARTIDA"
          className={`w-1/3 px-3 py-2 bg-neutral-800 border rounded-lg text-white text-base font-mono ${theme.focus} border-neutral-600`}
          disabled={loading}
        />
      </div>

      {/* 2. Ubicaciones disponibles por geografía */}
      <div>
        <label className={`block text-lg font-bold mb-2 font-mono tracking-wider ${theme.text}`}>
          UBICACIONES
        </label>
        <div className="border border-neutral-600 rounded-lg p-4 bg-neutral-800/50">
          <DualListbox
            value={formData.ubicacionids || []}
            onChange={(ubicacionids) =>
              setFormData((prev) => ({ ...prev, ubicacionids }))
            }
            options={ubicacionesOptions}
            placeholder="SELECCIONAR UBICACIONES"
            disabled={loading}
            canFilter={true}
            themeColor={themeColor}
            availableLabel="DISPONIBLES"
            selectedLabel="SELECCIONADOS"
          />
        </div>
      </div>

      {/* 3. Usuarios con permisos para la geografía */}
      <div>
        <label className={`block text-lg font-bold mb-2 font-mono tracking-wider ${theme.text}`}>
          USUARIOS
        </label>
        <div className="border border-neutral-600 rounded-lg p-4 bg-neutral-800/50">
          <DualListbox
            value={formData.usuarioids || []}
            onChange={(usuarioids) =>
              setFormData((prev) => ({ ...prev, usuarioids }))
            }
            options={usuariosOptions}
            placeholder="SELECCIONAR USUARIOS"
            disabled={loading}
            canFilter={true}
            themeColor={themeColor}
            availableLabel="DISPONIBLES"
            selectedLabel="SELECCIONADOS"
          />
        </div>
      </div>

      {/* Botones */}
      <div className="flex gap-4 justify-center pt-4">
        <button
          type="button"
          onClick={onSave}
          disabled={loading || !isFormValid}
          className={`px-8 py-2 rounded-lg font-mono font-bold tracking-wider text-white transition-colors ${
            loading || !isFormValid
              ? 'opacity-50 cursor-not-allowed bg-neutral-600'
              : `${theme.button}`
          }`}
        >
          {loading ? t('loading') || 'GUARDANDO...' : isUpdate ? t('update') || 'ACTUALIZAR' : t('save') || 'GUARDAR'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className={`px-8 py-2 rounded-lg font-mono font-bold tracking-wider text-white transition-colors ${
            loading 
              ? 'opacity-50 cursor-not-allowed bg-neutral-600'
              : `${theme.button}`
          }`}
        >
          {t('cancel') || 'CANCELAR'}
        </button>
      </div>
    </div>
  );
};

export default CarpetaForm;
